/**
 * Lieferanten — eigene Seite, aufgebaut wie die Kundenseite (Benjamin,
 * 25.09.2026: „angelehnt an die Kundenseite, damit man sich intuitiver durch
 * die App bewegt"). Links die Liste, rechts das Blatt des Gewählten.
 *
 * Zwei Blickrichtungen, umschaltbar wie „Pflege | Kundenliste":
 * - **Lieferanten**, nach Warengruppe gruppiert. Solange einer keine Gruppe
 *   hat, schlägt Kontor eine vor (aus seinen Materialien, sonst dem Namen);
 *   „Vorschläge übernehmen" setzt sie in einem Zug.
 * - **Material & Maschinen**, nach Art und Kategorie gruppiert, je Zeile der
 *   Standardlieferant. Rechts dann: wer es liefern kann — dort ordnet man
 *   Lieferanten zu. So lässt sich das System aus beiden Richtungen pflegen.
 *
 * - **Pflanzen** (16.10.2026): die Pflanzenbibliothek — Stammdaten, Qualitäten, Preise und die
 *   Aufschläge je Gruppe (`components/lieferanten/Pflanzen.tsx`).
 *
 * Adresse: `?ansicht=material|pflanzen`, `?lieferant=<id>`, `?material=<id>`, `?pflanze=<id>|aufschlag`
 * — so kommen auch Links aus Projekt und Blatt an.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { fehlerText } from "@vimaana/ui";

import { LieferantDetail } from "../components/lieferanten/LieferantBlatt";
import MaterialDetail from "../components/lieferanten/MaterialDetail";
import MaterialWahl from "../components/lieferanten/MaterialWahl";
import { AufschlagBlatt, PflanzeDetail, PflanzenListe } from "../components/lieferanten/Pflanzen";
import LieferantSuche from "../components/lieferanten/LieferantSuche";
import { ArtikelPruefen } from "../components/lieferanten/ArtikelPruefen";
import { useArtikelOffen } from "../hooks/useArtikel";
import {
  useGruppenVorschlaege, useLieferantenUebersicht, useMaterialStammlisten, useMaterialUebersicht,
  type LieferantZeile, type MaterialUebersichtZeile,
} from "../hooks/useLieferanten";
import { telLink } from "../lib/notizen";
import "../styles/kunden.css";
import "../styles/post.css";
import "../styles/lieferanten.css";

type Ansicht = "lieferanten" | "material" | "pflanzen";

export default function LieferantenPage() {
  const [such, setSuch] = useSearchParams();
  const ansicht: Ansicht = such.get("ansicht") === "material" ? "material" : such.get("ansicht") === "pflanzen" ? "pflanzen" : "lieferanten";
  const lieferantId = such.get("lieferant") || "";
  const materialId = such.get("material") || "";
  const pflanzeId = such.get("pflanze") || "";
  // Neue Artikel aus Belegen prüfen (Migration 182) — rechts statt eines Blatts.
  const artikel = such.get("artikel") === "neu";
  const setzen = (werte: Record<string, string>) => setSuch((v) => {
    const neu = new URLSearchParams(v);
    for (const [k, w] of Object.entries(werte)) { if (w) neu.set(k, w); else neu.delete(k); }
    return neu;
  });
  const lieferanten = useLieferantenUebersicht();
  const materialien = useMaterialUebersicht(ansicht === "material" || !!materialId);
  const detailRef = useRef<HTMLDivElement>(null);
  const gewaehlt = artikel ? "artikel" : ansicht === "material" ? materialId : ansicht === "pflanzen" ? pflanzeId : lieferantId;
  // Telefon: das Blatt steht unter der Liste — beim Wählen dorthin springen.
  useEffect(() => {
    if (gewaehlt && window.matchMedia("(max-width: 900px)").matches) detailRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [gewaehlt]);

  const anzahl = ansicht === "material" ? (materialien.data?.materialien.length ?? 0) : (lieferanten.data?.lieferanten.length ?? 0);
  const titel = ansicht === "material" ? "Material & Maschinen" : ansicht === "pflanzen" ? "Pflanzenbibliothek" : "Nach Warengruppe";

  return (
    <div className="kunden-seite lf-seite">
      <div className="lf-seitenkopf">
        <h1>Lieferanten</h1>
        {ansicht !== "pflanzen" && <span className="lf-seitenzahl">{anzahl} {ansicht === "material" ? "Material & Maschinen" : "Lieferant(en)"}</span>}
      </div>

      <div className={"kunden-split" + (gewaehlt ? " ist-gewaehlt" : "")}>
        <div className="kunden-liste">
          <div className="lf-listenkopf">
            <div className="kunden-listhead-row">
              <h2>{titel}</h2>
              <div className="kunden-listhead-actions">
                <div className="lf-umschalter kunden-viewtoggle" role="group" aria-label="Ansicht wählen">
                  <button type="button" className={ansicht === "lieferanten" ? "ist-an" : ""} aria-pressed={ansicht === "lieferanten"}
                    onClick={() => setzen({ ansicht: "", material: "", pflanze: "" })}>Lieferanten</button>
                  <button type="button" className={ansicht === "material" ? "ist-an" : ""} aria-pressed={ansicht === "material"}
                    onClick={() => setzen({ ansicht: "material", lieferant: "", pflanze: "" })}>Material</button>
                  <button type="button" className={ansicht === "pflanzen" ? "ist-an" : ""} aria-pressed={ansicht === "pflanzen"}
                    onClick={() => setzen({ ansicht: "pflanzen", lieferant: "", material: "" })}>Pflanzen</button>
                </div>
              </div>
            </div>
          </div>
          {ansicht === "lieferanten"
            ? <LieferantenListe daten={lieferanten.data?.lieferanten ?? []} laedt={lieferanten.isPending} fehler={lieferanten.error} gewaehlt={lieferantId}
                onWahl={(id) => setzen({ lieferant: id, artikel: "" })} onArtikel={() => setzen({ artikel: "neu", lieferant: "" })} />
            : ansicht === "pflanzen"
              ? <PflanzenListe gewaehlt={pflanzeId} onWahl={(id) => setzen({ pflanze: id })} />
              : <MaterialListe daten={materialien.data?.materialien ?? []} laedt={materialien.isPending} fehler={materialien.error} gewaehlt={materialId} onWahl={(id) => setzen({ material: id })} />}
        </div>

        <div className="kunden-detail lf-detail-spalte" ref={detailRef}>
          {artikel && <ArtikelSeite onClose={() => setzen({ artikel: "" })} />}
          {!artikel && ansicht === "lieferanten" && lieferantId && (
            <LieferantDetail key={lieferantId} lieferantId={lieferantId} onClose={() => setzen({ lieferant: "" })}
              onMaterial={(id) => setzen({ ansicht: "material", material: id, lieferant: "" })} />
          )}
          {!artikel && ansicht === "material" && materialId && (
            <MaterialDetail key={materialId} materialId={materialId} onClose={() => setzen({ material: "" })}
              onLieferant={(id) => setzen({ ansicht: "", lieferant: id, material: "" })} />
          )}
          {!artikel && ansicht === "pflanzen" && pflanzeId === "aufschlag" && <AufschlagBlatt onClose={() => setzen({ pflanze: "" })} />}
          {!artikel && ansicht === "pflanzen" && pflanzeId && pflanzeId !== "aufschlag" && (
            <PflanzeDetail key={pflanzeId} pflanzeId={pflanzeId} onClose={() => setzen({ pflanze: "" })}
              onLieferant={(id) => setzen({ ansicht: "", lieferant: id, pflanze: "" })} />
          )}
          {!gewaehlt && (
            <div className="lf-leerblatt">
              <h3>{ansicht === "material" ? "Kein Material gewählt" : ansicht === "pflanzen" ? "Keine Pflanze gewählt" : "Kein Lieferant gewählt"}</h3>
              <p>
                {ansicht === "pflanzen"
                  ? "Wähle links eine Pflanze. Die Daten hat Claude recherchiert, mit Quelle — ein ≈ heißt: aus Fachwissen, bitte gegenprüfen. „Geprüft“ setzt ihr, Stichproben reichen. Neue Pflanzen von Lieferantenrechnungen erscheinen hier von selbst als „ungeprüft“."
                  : ansicht === "material"
                  ? "Wähle links ein Material oder eine Maschine. Rechts ordnest du dann Lieferanten zu — der mit ★ ist der Standard und im Projekt vorausgewählt."
                  : "Wähle links einen Lieferanten. Rechts pflegst du seine Materialien und Maschinen, Ansprechpartner und Warengruppe. Über „Material“ oben siehst du dieselbe Zuordnung vom Material her."}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Liste: Lieferanten nach Warengruppe ──────────────────────────────────

/** Neue Artikel aus Belegen — alle Lieferanten, je Artikel einmal. */
function ArtikelSeite({ onClose }: { onClose: () => void }) {
  const q = useArtikelOffen();
  const zeilen = q.data?.zeilen ?? [];
  return (
    <div className="lf-detail">
      <header className="lf-detail-kopf">
        <div>
          <p className="lf-detail-ueber">Aus den Belegen</p>
          <h2>Neue Artikel</h2>
        </div>
        <button type="button" className="lf-knopf lf-detail-zu" onClick={onClose} aria-label="Schließen">×</button>
      </header>
      <p className="lf-hinweis">
        Positionen aus Rechnungen und Kassenbons, die bei ihrem Lieferanten noch nicht in der Liste stehen.
        Einmal entscheiden — denselben Artikel erkennt Kontor danach selbst, samt Preis. Fracht, Pfand und
        Leistungen fragt es gar nicht erst.
      </p>
      {q.isPending && <p className="lf-leer">Lade …</p>}
      {!!q.error && <p className="lf-fehler">{fehlerText(q.error)}</p>}
      {!q.isPending && zeilen.length === 0 && <p className="lf-leer">Alles entschieden — nichts Neues.</p>}
      <ArtikelPruefen zeilen={zeilen} mitFirma />
    </div>
  );
}

function LieferantenListe({ daten, laedt, fehler, gewaehlt, onWahl, onArtikel }: {
  daten: LieferantZeile[]; laedt: boolean; fehler: unknown; gewaehlt: string; onWahl: (id: string) => void; onArtikel: () => void;
}) {
  const artikelOffen = useArtikelOffen();
  const neueArtikel = artikelOffen.data?.zeilen.length ?? 0;
  const listen = useMaterialStammlisten();
  const uebernehmen = useGruppenVorschlaege();
  const [q, setQ] = useState("");
  const [neu, setNeu] = useState(false);
  const gruppen = listen.data?.gruppen ?? [];
  const label = (slug: string) => gruppen.find((g) => g.slug === slug)?.label || slug;
  const s = q.trim().toLowerCase();
  const gefiltert = daten.filter((l) => !s || [l.name, l.adresse, l.email, l.telefon, ...(l.gruppen || []).map(label)].some((t) => (t || "").toLowerCase().includes(s)));
  const bloecke = useMemo(() => {
    const reihe = [...gruppen.map((g) => g.slug), ""];
    // Ein Lieferant steht in JEDER seiner Warengruppen (Migration 218) — wer
    // Steine sucht, soll den Baustoffhandel dort auch finden.
    return reihe.map((slug) => ({
      slug,
      zeilen: gefiltert.filter((l) => (slug ? (l.gruppen || []).includes(slug) : !(l.gruppen || []).length)),
    })).filter((b) => b.zeilen.length);
  }, [gefiltert, gruppen]);
  const mitVorschlag = daten.filter((l) => !(l.gruppen || []).length && l.gruppe_vorschlag).length;

  return (
    <>
      <div className="lf-listensuche">
        <input className="lf-feld" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name, Adresse, Mail, Telefon, Gruppe …" aria-label="Lieferant suchen" autoComplete="off" />
        {/* Benjamin, 25.09.2026: „auf der Lieferantenseite fehlt eine Möglichkeit, neue
            Lieferanten anzulegen" — bis dahin nur versteckt im Suchfeld. */}
        <button type="button" className="lf-knopf is-primaer" onClick={() => setNeu((v) => !v)}>＋ Neu</button>
      </div>
      {neu && (
        <div className="lf-rand">
          <LieferantSuche nurBeiEingabe platzhalter="Name des neuen Lieferanten …" onAbbruch={() => setNeu(false)}
            hinweis="Tippen — gibt es ihn schon, steht er darunter; sonst „als neuen Lieferanten anlegen“."
            onWahl={(id) => { setNeu(false); setQ(""); onWahl(id); }} />
        </div>
      )}
      {neueArtikel > 0 && (
        <div className="lf-vorschlagleiste">
          <span>{neueArtikel} neue Artikel aus Belegen.</span>
          <button type="button" className="lf-knopf" onClick={onArtikel}>Prüfen</button>
        </div>
      )}
      {mitVorschlag > 0 && (
        <div className="lf-vorschlagleiste">
          <span>{mitVorschlag} Lieferanten ohne Gruppe haben einen Vorschlag.</span>
          <button type="button" className="lf-knopf" disabled={uebernehmen.isPending} onClick={() => uebernehmen.mutate(undefined)}>
            {uebernehmen.isPending ? "Übernimmt …" : "Vorschläge übernehmen"}
          </button>
        </div>
      )}
      <div className="kunden-tabelle">
        {laedt && <p className="lf-leer lf-rand">Lade …</p>}
        {!!fehler && <p className="lf-fehler lf-rand">{fehlerText(fehler)}</p>}
        {bloecke.map((b) => (
          <section key={b.slug || "ohne"} className="lf-block">
            <h3 className="lf-blockkopf">{b.slug ? label(b.slug) : "Ohne Gruppe"} <span>{b.zeilen.length}</span></h3>
            <ul className="lf-tabelle">
              {b.zeilen.map((l) => (
                <li key={l.id} className={"lf-tzeile" + (gewaehlt === l.id ? " ist-gewaehlt" : "")}>
                  <button type="button" className="lf-tzeile-haupt" onClick={() => onWahl(l.id)}>
                    <span className="lf-tzeile-name">{l.name}</span>
                    <span className="lf-tzeile-info">
                      {[l.materialien ? `${l.materialien} Material${l.standard ? ` · ${l.standard}★` : ""}` : "",
                        l.projekte_laufend ? `${l.projekte_laufend} laufend` : "",
                        l.kontakte ? `${l.kontakte} Kontakt${l.kontakte === 1 ? "" : "e"}` : "",
                        (l.gruppen || []).length > 1 ? "auch " + (l.gruppen || []).filter((g) => g !== b.slug).map(label).join(", ") : "",
                        !(l.gruppen || []).length && l.gruppe_vorschlag ? `Vorschlag: ${label(l.gruppe_vorschlag)}` : ""].filter(Boolean).join(" · ") || "—"}
                    </span>
                  </button>
                  {l.telefon && <a className="lf-knopf lf-tzeile-tel" href={telLink(l.telefon)} title={`${l.telefon} anrufen`}>✆</a>}
                </li>
              ))}
            </ul>
          </section>
        ))}
        {!laedt && bloecke.length === 0 && <p className="lf-leer lf-rand">Kein Lieferant passt.</p>}
      </div>
    </>
  );
}

// ── Liste: Material & Maschinen ──────────────────────────────────────────

function MaterialListe({ daten, laedt, fehler, gewaehlt, onWahl }: {
  daten: MaterialUebersichtZeile[]; laedt: boolean; fehler: unknown; gewaehlt: string; onWahl: (id: string) => void;
}) {
  const listen = useMaterialStammlisten();
  const [q, setQ] = useState("");
  const [nurOhne, setNurOhne] = useState(false);
  const [neu, setNeu] = useState(false);
  const kat = (slug: string) => listen.data?.kategorien.find((k) => k.slug === slug)?.label || slug;
  const s = q.trim().toLowerCase();
  const gefiltert = daten.filter((m) => (!nurOhne || !m.standard_id)
    && (!s || [m.name, m.standard_name, kat(m.kategorie)].some((t) => (t || "").toLowerCase().includes(s))));
  const bloecke = useMemo(() => {
    const aus = new Map<string, MaterialUebersichtZeile[]>();
    for (const m of gefiltert) {
      const k = m.art === "maschine" ? "~maschinen" : (m.kategorie || "~ohne");
      aus.set(k, [...(aus.get(k) ?? []), m]);
    }
    const reihe = [...(listen.data?.kategorien ?? []).map((k) => k.slug), "~ohne", "~maschinen"];
    return [...aus.entries()].sort((a, b) => reihe.indexOf(a[0]) - reihe.indexOf(b[0]));
  }, [gefiltert, listen.data]);
  const ohneStandard = daten.filter((m) => !m.standard_id).length;

  return (
    <>
      <div className="lf-listensuche">
        <input className="lf-feld" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Material, Maschine, Lieferant …" aria-label="Material suchen" autoComplete="off" />
        <button type="button" className={"lf-knopf" + (nurOhne ? " ist-an" : "")} aria-pressed={nurOhne} onClick={() => setNurOhne((v) => !v)}
          title="Nur Materialien ohne Standardlieferant">Ohne Lieferant{ohneStandard ? ` (${ohneStandard})` : ""}</button>
        <button type="button" className="lf-knopf is-primaer" onClick={() => setNeu((v) => !v)}>＋ Neu</button>
      </div>
      {neu && (
        <div className="lf-rand">
          <MaterialWahl platzhalter="Name des neuen Materials / der Maschine …" onAbbruch={() => setNeu(false)}
            onWahl={(m) => { setNeu(false); onWahl(m.id); }} />
        </div>
      )}
      <div className="kunden-tabelle">
        {laedt && <p className="lf-leer lf-rand">Lade …</p>}
        {!!fehler && <p className="lf-fehler lf-rand">{fehlerText(fehler)}</p>}
        {bloecke.map(([k, zeilen]) => (
          <section key={k} className="lf-block">
            <h3 className="lf-blockkopf">{k === "~maschinen" ? "Maschinen & Geräte" : k === "~ohne" ? "Ohne Kategorie" : kat(k)} <span>{zeilen.length}</span></h3>
            <ul className="lf-tabelle">
              {zeilen.map((m) => (
                <li key={m.id} className={"lf-tzeile" + (gewaehlt === m.id ? " ist-gewaehlt" : "")}>
                  <button type="button" className="lf-tzeile-haupt" onClick={() => onWahl(m.id)}>
                    <span className="lf-tzeile-name">{m.name}{m.einheit && <small> · {m.einheit}</small>}</span>
                    <span className={"lf-tzeile-info" + (m.standard_id ? "" : " ist-offen")}>
                      {m.standard_id ? `★ ${m.standard_name}` : "kein Standardlieferant"}
                      {m.lieferanten > 1 ? ` · ${m.lieferanten} Lieferanten` : ""}
                      {m.projekte_laufend ? ` · ${m.projekte_laufend} laufend` : ""}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        ))}
        {!laedt && bloecke.length === 0 && <p className="lf-leer lf-rand">Nichts passt.</p>}
      </div>
    </>
  );
}
